import React, { useEffect, useState } from "react";
import {
  Container,
  ListGroup,
  ListGroupItem,
  Button,
  Form,
  Collapse,
} from "react-bootstrap";
import { Accordion, Card } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { MultiSelect } from "react-multi-select-component";
import moment from "moment";
import { FaMinus } from "react-icons/fa";
import minus_icon from "../../assets/images/icon/cross-plain.svg";
import plus_icon from "../../assets/images/icon/plus.svg";
import copy_icon from "../../assets/images/icon/Copy-icon.svg";
import delete_icon from "../../assets/images/icon/delete.svg";
import clock from "../../assets/images/icon/clock.svg";
import ButtonLoader from "components/Common/ButtonLoader";
import api from "../../api"
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const TimeslotBooking = ({
  onValueChange,
  onErrorHandle,
  defaultTimeSlots,
}) => {
  const { t } = useTranslation();
  const initialTimeSlots = {
    Sunday: [],
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
  };

  const generateTimeSlots = () => {
    const timeSlots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let min = 0; min < 60; min += 30) {
        const hourStr = hour.toString().padStart(2, "0");
        const minStr = min.toString().padStart(2, "0");
        timeSlots.push(`${hourStr}:${minStr}`);
      }
    }
    return timeSlots;
  };

  const [timeSlots, setTimeSlots] = useState(() => {
    const initialState = { ...initialTimeSlots };
    Object.keys(initialTimeSlots).forEach((day, index) => {
      initialState[day] = defaultTimeSlots[index] || [];
    });
    return initialState;
  });
  const [selectedTargetDays, setSelectedTargetDays] = useState({});
  const [pendingSourceDay, setPendingSourceDay] = useState("");
  const [errors, setErrors] = useState({}); // State to track validation errors
  const [value, setValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [daytimeSlots, setDaytimeSlots] = useState("");
  const [ErrorsTimeslots, setErrorsTimeslots] = useState({}); // State to track validation errors

  // Day to integer map
  const dayToIntegerMap = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };

  // Update timeSlots when defaultTimeSlots change
  useEffect(() => {
    if (defaultTimeSlots) {
      const updatedTimeSlots = { ...initialTimeSlots };
      Object.keys(initialTimeSlots).forEach((day, index) => {
        updatedTimeSlots[day] = defaultTimeSlots[index] || [];
      });
      setTimeSlots(updatedTimeSlots); // Update the state with new time slots
    }
  }, [defaultTimeSlots]);

  useEffect(() => {
    setValue(JSON.stringify(timeSlots));
    onValueChange(JSON.stringify(timeSlots)); // Notify the parent component about the state change
  }, [timeSlots]);

  useEffect(() => {
    onErrorHandle(errors); // Notify the parent component about the state change
  }, [errors]);

  const handleDayChange = day => {
    const startTime = "09:00";
    setTimeSlots(prev => ({
      ...prev,
      [day]:
        prev[day].length > 0
          ? []
          : [
              {
                startTime,
                endTime: moment(startTime, "HH:mm")
                  .add(30, "minutes")
                  .format("HH:mm"),
              },
            ],
    }));
    setErrors(prev => ({ ...prev, [day]: [] })); // Reset errors for this day
  };

  // console.log(timeSlots);

  const checkOverlap = (day, newStartTime, newEndTime, indexToExclude = -1) => {
    const daySlots = timeSlots[day];
    for (let i = 0; i < daySlots.length; i++) {
      if (i === indexToExclude) continue; // Skip checking the slot being edited
      const { startTime, endTime } = daySlots[i];
      if (
        (newStartTime >= startTime && newStartTime < endTime) || // New start overlaps existing
        (newEndTime > startTime && newEndTime <= endTime) || // New end overlaps existing
        (newStartTime <= startTime && newEndTime >= endTime) // New slot surrounds existing
      ) {
        return true;
      }
    }
    return false;
  };

  const handleTimeslotChange = (day, index, field, value) => {
    setTimeSlots(prev => {
      const updatedTimeSlots = prev[day].map((slot, i) =>
        i === index ? { ...slot, [field]: value } : slot
      );

      const currentSlot = updatedTimeSlots[index];
      const { startTime, endTime } = currentSlot;

      let newErrors = { ...errors };

      // Check for "End time cannot be less than start time"
      if (field === "endTime" && startTime && value < startTime) {
        if (!newErrors[day]) newErrors[day] = [];
        newErrors[day][index] = t(`Timeslot Booking.Timeslots err 1`);
      } else if (field === "startTime" && endTime && endTime < value) {
        if (!newErrors[day]) newErrors[day] = [];
        newErrors[day][index] = t(`Timeslot Booking.Timeslots err 2`);
      } else if (
        startTime &&
        endTime &&
        checkOverlap(day, startTime, endTime, index)
      ) {
        // If no start/end time error, check for overlap
        if (!newErrors[day]) newErrors[day] = [];
        newErrors[day][index] = t(`Timeslot Booking.Timeslots err 3`);
      } else {
        // Clear error if no validation fails
        if (newErrors[day]) {
          newErrors[day][index] = ""; // Clear specific error
          // If all errors for the day are empty, clear the day from errors
          if (newErrors[day].every(error => error === "")) {
            delete newErrors[day];
          }
        }
      }

      setErrors(newErrors);

      return {
        ...prev,
        [day]: updatedTimeSlots,
      };
    });
  };

  const addTimeslot = day => {
    const lastIndexVal = timeSlots[day][timeSlots[day].length - 1];
    const startTime = lastIndexVal.endTime ? lastIndexVal.endTime : "09:00";
    setTimeSlots(prev => ({
      ...prev,
      [day]: [
        ...prev[day],
        {
          startTime,
          endTime: moment(startTime, "HH:mm")
            .add(30, "minutes")
            .format("HH:mm"),
        },
      ],
    }));
    setErrors(prev => ({ ...prev, [day]: [...(prev[day] || []), ""] })); // Add an empty error state for the new slot
  };

  const removeTimeslot = (day, index) => {
    setTimeSlots(prev => {
      const updatedTimeSlots = prev[day].filter((_, i) => i !== index);
      return {
        ...prev,
        [day]: updatedTimeSlots,
      };
    });

    // Remove error when timeslot is removed
    setErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors[day]) newErrors[day].splice(index, 1);
      return newErrors;
    });
  };

  const handleTargetDaysChange = (day, selectedOptions) => {
    setSelectedTargetDays(prev => ({
      ...prev,
      [day]: selectedOptions,
    }));
    setPendingSourceDay(day);
  };

  const applyTimeslots = () => {
    if (pendingSourceDay && selectedTargetDays[pendingSourceDay].length > 0) {
      setTimeSlots(prev => {
        const updatedTimeSlots = { ...prev };
        const sourceTimeslots = prev[pendingSourceDay];

        selectedTargetDays[pendingSourceDay].forEach(option => {
          const targetDay = option.value;
          if (targetDay !== pendingSourceDay) {
            updatedTimeSlots[targetDay] = sourceTimeslots;
          }
        });
        setSelectedTargetDays([]);
        return updatedTimeSlots;
      });
    }
  };

  // Update the day options to disable the selected days
  const dayOptions = Object.keys(timeSlots).map(day => ({
    label: t(`Days.${day}`),
    value: day,
    disabled: timeSlots[day].length > 0, // Disable option if day is selected
  }));

  const timeSlotOptions = generateTimeSlots();

  const handleTimeslotSubmit = event => {
    event.preventDefault();

    setErrors({});
    let errorObj = {};
    const errorMessages = [];

    let payload = new FormData();
    // console.log(timeSlots, "timeSlots");

    const mappedTimeSlots = Object.fromEntries(
      Object.entries(timeSlots)
        .map(([day, slots]) => {
          const integerKey = dayToIntegerMap[day];
          if (integerKey === undefined) return;
          return [integerKey, slots];
        })
        .filter(entry => entry !== undefined)
    );

    // console.log(mappedTimeSlots, "mappedTimeSlots");

    // Convert the mappedTimeSlots object to a JSON string before appending
    // const timeSlotsJSON = JSON.stringify(mappedTimeSlots);

    const timeSlotsobj = JSON.stringify(mappedTimeSlots, null, 2);
    payload.append("timeSlots", timeSlotsobj);

    // Log the FormData contents for debugging
    payload.forEach((value, key) => {
      // console.log(`${key}: ${value}`);
    });

    // Loop through each day in the ErrorsTimeslots object and extract the messages
    for (const [day, messages] of Object.entries(errors)) {
      if (Array.isArray(messages) && messages.length > 0) {
        errorObj[day] = messages; // Keep track of errors per day
        messages.forEach(message => {
          if (message && message.trim() !== "") {
            errorMessages.push(`${day}: ${message}`);
          }
        });
      } else {
        delete errors[day];
      }
    }

    // console.log(errors, "errors");
    // console.log(errorMessages, "errorMessages");

    if (errorMessages.length > 0) {
      toast.error(errorMessages.join("\n")); // Display each error on a new line
      setErrors(errorObj); // Set the error state for the form
      setIsLoading(false);
    } else {
      // Proceed to call the API if the payload is correct
      updateUserTimeslots(payload);
    }
  };

  // Function to update user
  const updateUserTimeslots = payload => {
    const userId = localStorage.getItem("user_id");
    if (!userId || userId === "0") {
      return; // Exit the function if userId is invalid
    }
    // console.log("API called!!!");
    // console.log(payload, "payload");

    api
      .patch(
        `${process.env.REACT_APP_API_URL}/admin/user/update-usertimeslots/${userId}`,
        payload,
      )
      .then(response => {
        setIsLoading(false);
        // Show success toast
        toast.success(t(`Timeslot Booking.Timeslots updated`));
      })
      .catch(err => {
        setIsLoading(false);
        console.log("Error:", err);

        // Show error toast (optional)
        toast.error(t(`Timeslot Booking.Timeslots update failed`));
      });
  };

  const [activeIndex, setActiveIndex] = useState(null);

  const toggleAccordion = index => {
    // Toggle the active index
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="time-slot-task pt-0">
      <h3>{t(`Timeslot Booking.Weekly Hours`)}</h3>
      <ListGroup>
        {Object.keys(timeSlots).map(day => (
          <ListGroupItem key={day} className="mb-3 border rounded">
            <div className="d-flex justify-content-between align-items-center  card-slot-timecard">
              <div className="time-slot-card">
                <Form.Check
                  type="checkbox"
                  id={`day-checkbox-${day}`} // Added unique id for accessibility
                  checked={timeSlots[day].length > 0}
                  onChange={() => handleDayChange(day)}
                  label={t(`Days.${day}`)} // Ensure this is not empty or undefined
                />

                <div className="icon-copy">
                  <div className="icon-copys">
                    <img src={copy_icon} alt="copy icon" />
                  </div>
                  <label id="copy-times-label" className="sr-only">
                    {t(`Timeslot Booking.Copy times`)}{" "}
                    {/* This label will be referenced by aria-labelledby */}
                  </label>
                  <div
                    title={
                      selectedTargetDays.length
                        ? `${selectedTargetDays
                            .map(option => option.label || option.name)
                            .join(", ")}`
                        : `${t("Timeslot Booking.Copy times")}`
                    }
                  >
                    <MultiSelect
                      options={dayOptions}
                      value={selectedTargetDays[day] || []}
                      onChange={selectedOptions =>
                        handleTargetDaysChange(day, selectedOptions)
                      }
                      labelledBy="copy-times-label"
                      hasSelectAll={false}
                      disableSearch={true}
                      overrideStrings={{
                        selectSomeItems: t(`Timeslot Booking.Copy times`),
                      }}
                    />
                  </div>
                </div>

                <div className="time-slot-booking-card-ntn ">
                  {timeSlots[day].length > 0 &&
                    selectedTargetDays[day]?.length > 0 && (
                      <Button
                        className="mt-0"
                        variant="primary"
                        onClick={applyTimeslots}
                      >
                        {t(`Timeslot Booking.Apply`)}
                      </Button>
                    )}
                </div>
              </div>

              <div className="time-slot-booking-card-ntn pluss">
                {timeSlots[day].length > 0 && (
                  <Button variant="primary" onClick={() => addTimeslot(day)}>
                    <img src={plus_icon} alt="plus" />
                  </Button>
                )}
              </div>
            </div>
            <Collapse in={timeSlots[day].length > 0}>
              <div className="timeslot-bane spo">
                <div className="time-card-index">
                  {timeSlots[day].length === 0 ? (
                    <span className="no-data-message">
                      {t(`Timeslot Booking.No Data`)}
                    </span>
                  ) : (
                    <Accordion>
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>
                          Add Weekly Availability
                        </Accordion.Header>
                        <Accordion.Body>
                          {timeSlots[day].map((slot, index) => (
                            <div
                              key={index}
                              className="d-flex align-items-center gap-2  timeslot-add-c"
                            >
                              <div className="time-slot-booking-card position-relative">
                                <span>
                                  <img src={clock} alt="clock" />
                                </span>
                                <div title="Timeslot Booking.Select Start Time">
                                  <Form.Control
                                    as="select"
                                    value={slot.startTime}
                                    onChange={e =>
                                      handleTimeslotChange(
                                        day,
                                        index,
                                        "startTime",
                                        e.target.value
                                      )
                                    }
                                  >
                                    <option value="">
                                      {t(`Timeslot Booking.Select Start Time`)}
                                    </option>
                                    {timeSlotOptions.map(option => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </Form.Control>
                                </div>
                              </div>
                              <div className="time-slot-booking-card position-relative">
                                <span>
                                  <img src={clock} alt="clock" />
                                </span>
                                <div title="Timeslot Booking.Select End Time      ">
                                  <Form.Control
                                    as="select"
                                    value={slot.endTime}
                                    onChange={e =>
                                      handleTimeslotChange(
                                        day,
                                        index,
                                        "endTime",
                                        e.target.value
                                      )
                                    }
                                  >
                                    <option value="">
                                      {t(`Timeslot Booking.Select End Time`)}
                                    </option>
                                    {timeSlotOptions.map(option => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </Form.Control>
                                </div>
                              </div>

                              <div className="time-slot-booking-card cross">
                                <Button
                                  variant="danger"
                                  onClick={() => removeTimeslot(day, index)}
                                >
                                  <img src={delete_icon} alt="delete" />
                                </Button>
                              </div>

                              {/* Display error message */}
                              {errors[day] && errors[day][index] && (
                                <div
                                  className="text-danger ml-2"
                                  style={{
                                    flex: "1 1 100%",
                                    minWidth: "200px",
                                  }}
                                >
                                  {errors[day][index]}
                                </div>
                              )}
                            </div>
                          ))}
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  )}
                </div>
              </div>
            </Collapse>
          </ListGroupItem>
        ))}
      </ListGroup>

      <Form.Group className="cta text-center gap-3 d-flex justify-content-left mt-4 mb-4">
        <ButtonLoader
          title={t(`Timeslot Booking.Save Details`)}
          variant="primary"
          style={{ marginLeft: "5px" }}
          type="submit"
          loading={isLoading}
          message={t(`Timeslot Booking.Saving`)}
          onClick={e => {
            handleTimeslotSubmit(e);
          }}
        />
      </Form.Group>
    </div>
  );
};

export default TimeslotBooking;
